<template>
  <v-stepper-vertical-item
    :value="stepValue"
    :elevation="0"
    :class="{ 'step-completed': currentStep > stepValue }"
  >
    <template #title>
      <div class="subtitle1">{{ $t("modals.allowCloudService.step1.title") }}</div>
    </template>
    <template #icon>
      <v-icon v-if="currentStep > stepValue" icon="$check" />
      <span v-else>{{ stepNumber }}</span>
    </template>
    <div>
      <p
        class="body2 mb-4"
        v-html="
          $t('modals.allowCloudService.step1.description', {
            service: isOtherProvider
              ? $t(`modals.allowCloudService.step1.emailProvider`)
              : $t(`services.${modelValue.selectedService}`),
          })
        "
      />

      <a :href="guideLink" target="_blank" class="d-flex align-center mb-6">
        <span class="coro-link body2">{{
          $t("modals.allowCloudService.step1.guide", {
            service: isOtherProvider
              ? $t(`modals.allowCloudService.step1.otherDomains`)
              : $t(`services.${modelValue.selectedService}`),
          })
        }}</span>
        <v-icon icon="$externalLink" class="ml-1" size="16" />
      </a>
      <div class="mt-5">
        <v-btn
          color="primary"
          rounded
          :loading="loading"
          :disabled="disable"
          @click="moveToNextStep"
        >
          {{ $t("modals.allowCloudService.step1.actionBtn") }}
        </v-btn>
        <v-btn class="ml-4" @click="goBack">{{ $t("general.back") }}</v-btn>
      </div>
    </div>
  </v-stepper-vertical-item>
</template>

<script lang="ts">
import { type PropType, computed, defineComponent, ref } from "vue";
import { Service } from "@/constants/cloud-apps.ts";
import {
  SatConfigurationStatus,
  SatServiceProvider,
  SatServiceProviderToServiceMap,
} from "@/constants/security-awareness.ts";
import { useSecurityAwarenessModule } from "@/_store/security-awareness/security-awareness.module.ts";
import type { SatConnectionConfigForm } from "@/components/modals/security-awareness/allow-security-awareness.types.ts";

export default defineComponent({
  props: {
    currentStep: {
      type: Number,
      required: true,
    },
    stepValue: {
      type: Number,
      required: true,
    },
    stepNumber: {
      type: Number,
      required: true,
    },
    modelValue: {
      type: Object as PropType<SatConnectionConfigForm>,
      required: true,
    },
    disable: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["next", "back"],
  setup(props, { emit }) {
    const securityAwarenessStore = useSecurityAwarenessModule();
    const loading = ref(false);
    const guideLink = "https://docs.coro.net/sat/allowlist/";

    const formValue = computed(() => props.modelValue);

    const isOtherProvider = computed(() => {
      return formValue.value.selectedService === "other";
    });

    const satConfigurationProvider = computed(
      () =>
        SatServiceProviderToServiceMap[formValue.value.selectedService as Service] ??
        SatServiceProvider.CUSTOM
    );

    const moveToNextStep = async () => {
      loading.value = true;
      await securityAwarenessStore.updateSatConfig(
        {
          status: SatConfigurationStatus.ALLOWED,
        },
        satConfigurationProvider.value
      );
      loading.value = false;
      emit("next");
    };

    const goBack = () => {
      emit("back");
    };

    return {
      guideLink,
      loading,
      isOtherProvider,
      moveToNextStep,
      goBack,
    };
  },
});
</script>
